import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faListUl, faLayerGroup, faStethoscope, faBowlFood, 
  faBasketShopping, faSearch, faBoxOpen 
} from '@fortawesome/free-solid-svg-icons';
import './ProductsPage.css';
import { buildProductGallery, resolveImageSrc } from '../../utils/productGallery';
import { PLACEHOLDER_300 } from '../../utils/imagePlaceholder';
import { fetchWithAuth } from '../../utils/authUtils';
import { cartCount, subscribeCart } from '../../utils/cartStore';
import { isProductInStock, productStockQty, stockStatusClass, stockStatusLabel } from '../../utils/productStock';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';
const ALL_CATEGORY = 'Tất cả';

const ICON_BY_KEY = {
  pill: faBowlFood,
  heart: faStethoscope,
  spa: faBasketShopping,
  medical: faStethoscope,
  food: faBowlFood,
};

const pickCategoryIcon = (iconKey) => ICON_BY_KEY[String(iconKey || '').toLowerCase()] || faBasketShopping;

const isAllCategory = (value) => {
  const v = String(value || '').trim();
  return !v || v.toLowerCase() === 'all' || v === ALL_CATEGORY;
};

const ProductsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([{ name: ALL_CATEGORY, icon: faLayerGroup }]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [itemsInCart, setItemsInCart] = useState(cartCount());

  const rawCat = searchParams.get('cat');
  const cat = isAllCategory(rawCat) ? ALL_CATEGORY : rawCat;
  const search = searchParams.get('search') || '';
  const sort = searchParams.get('sort') || '';

  // Fetch categories from API (sync with DB)
  useEffect(() => subscribeCart(() => setItemsInCart(cartCount())), []);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const response = await fetchWithAuth(`${API_BASE}/api/categories`);
        const data = await response.json();
        if (!data?.success || !Array.isArray(data.data)) return;

        const fromApi = data.data.map((item) => ({
          name: item.name,
          icon: pickCategoryIcon(item.icon),
        }));
        setCategories([{ name: ALL_CATEGORY, icon: faLayerGroup }, ...fromApi]);
      } catch (_) {
        // keep default "Tất cả" only
      }
    };
    loadCategories();
  }, []);

  // Fetch products
  useEffect(() => {
    fetchProducts();
  }, [cat, search, sort]);

  const fetchProducts = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (!isAllCategory(cat)) params.append('category', cat);
      if (search) params.append('search', search);
      if (sort) params.append('sort', sort);

      const response = await fetchWithAuth(`${API_BASE}/api/products?${params.toString()}`);
      const data = await response.json();
      
      if (data.success) {
        setProducts(data.data || []);
      } else {
        setError(data.message || 'Lỗi khi tải sản phẩm');
      }
    } catch (err) {
      setError('Không thể kết nối đến server');
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryClick = (category) => {
    const params = new URLSearchParams();
    if (!isAllCategory(category)) params.append('cat', category);
    if (search) params.append('search', search);
    if (sort) params.append('sort', sort);
    setSearchParams(params);
  };

  const handleSortClick = (sortType) => {
    const params = new URLSearchParams();
    if (!isAllCategory(cat)) params.append('cat', cat);
    if (search) params.append('search', search);
    if (sortType) params.append('sort', sortType);
    setSearchParams(params);
  };

  const handleSearchChange = (e) => {
    const params = new URLSearchParams();
    if (!isAllCategory(cat)) params.append('cat', cat);
    if (e.target.value) params.append('search', e.target.value);
    if (sort) params.append('sort', sort);
    setSearchParams(params);
  };

  const handleProductClick = (productId) => {
    navigate(`/shop/product/${productId}`);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('vi-VN').format(price);
  };

  return (
    <div className="shop-page">
      {/* Header sẽ được include từ layout */}
      
      <div className="store-container">
        {/* Sidebar */}
        <aside className="sidebar">
          <h3><FontAwesomeIcon icon={faListUl} /> Danh mục</h3>
          <ul className="category-list">
            {categories.map((category) => (
              <li key={category.name} className="category-item">
                <button
                  className={`category-link ${cat === category.name ? 'active' : ''}`}
                  onClick={() => handleCategoryClick(category.name)}
                >
                  <FontAwesomeIcon icon={category.icon} />
                  {category.name}
                </button>
              </li>
            ))}
          </ul>
        </aside>

        {/* Main Content */}
        <main className="main-content">
          <div className="shop-shortcuts">
            <button type="button" onClick={() => navigate('/shop/cart')}>Giỏ hàng ({itemsInCart})</button>
            <button type="button" onClick={() => navigate('/shop/orders')}>Đơn hàng của tôi</button>
          </div>
          <div className="filter-bar">
            <div className="sort-options">
              <span>Sắp xếp theo:</span>
              <button
                className={`sort-btn ${sort === 'price_asc' ? 'active' : ''}`}
                onClick={() => handleSortClick('price_asc')}
              >
                Giá: Thấp - Cao
              </button>
              <button
                className={`sort-btn ${sort === 'price_desc' ? 'active' : ''}`}
                onClick={() => handleSortClick('price_desc')}
              >
                Giá: Cao - Thấp
              </button>
            </div>
            <div className="search-box">
              <FontAwesomeIcon icon={faSearch} className="search-icon" />
              <input
                type="text"
                value={search}
                onChange={handleSearchChange}
                placeholder="Tìm sản phẩm..."
              />
            </div>
          </div>

          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Đang tải sản phẩm...</p>
            </div>
          ) : error ? (
            <div className="error-state">
              <p>{error}</p>
              <button onClick={fetchProducts} className="btn-retry">Thử lại</button>
            </div>
          ) : products.length === 0 ? (
            <div className="empty-state">
              <FontAwesomeIcon icon={faBoxOpen} size="3x" />
              <p>Không tìm thấy sản phẩm nào phù hợp.</p>
            </div>
          ) : (
            <div className="product-grid">
              {products.map((product) => {
                const cover = buildProductGallery(product)[0];
                const stock = productStockQty(product);
                const inStock = isProductInStock(product);
                return (
                <div
                  key={product.id}
                  className={`product-card ${inStock ? '' : 'product-card--out-of-stock'}`}
                  onClick={() => handleProductClick(product.id)}
                >
                  <div className={`product-stock-badge ${stockStatusClass(stock)}`}>{stockStatusLabel(stock)}</div>
                  <img
                    src={cover ? resolveImageSrc(cover) : PLACEHOLDER_300}
                    alt={product.name}
                    className="product-card-cover"
                    onError={(e) => {
                      e.target.src = PLACEHOLDER_300;
                    }}
                  />
                  <div className="p-info">
                    <div className="p-name">{product.name}</div>
                    <div className="p-footer">
                      <span className="p-price">₫{formatPrice(product.price)}</span>
                      <button className="btn-view" type="button">{inStock ? 'Chi tiết' : 'Xem SP'}</button>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default ProductsPage;
